/* oxlint-disable react/no-danger */

import { clsx } from 'clsx';
import { type FC } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { Ticker } from 'shared/components/Ticker';

import { type TGetMainpageBrandCompleteContextResult } from '../../utils/getMainpageBrandCompleteContext';

import styles from './MainpageBrandDesktopLegacy.css';

export type TMainpageBrandDesktopLegacyProps = {
  completeContext: TGetMainpageBrandCompleteContextResult;
};

/**
 * Legacy-верстка десктопного баннера главной (`mainpageBrand`).
 *
 * Повторяет исходный дизайн из frontend-mainpage (`MediaBanner`): вместо единого
 * оборачивающего `<a>` (как в `MainpageBrandDesktop`) внешний контейнер `promo` —
 * обычный `<div>`, а оффер, логотип, заголовок с описанием и кнопка — отдельные
 * ссылки `<a>`. Переключается фича-флагом `MarTech.MainpageBrandDesktopLegacyDesign.Enabled`
 * в `MainpageBrandContainer`.
 */
export const MainpageBrandDesktopLegacy: FC<TMainpageBrandDesktopLegacyProps> = ({
  completeContext: {
    url,
    bgImageUrl,
    logoImageUrl,
    substrateNotNeeded,
    buttonText,
    label,
    note,
    sanitizedName,
    sanitizedInfo,
    sanitizedActionText,
    sanitizedPhone,
    mortgageWarning,
    mortgageTermsWarning,
    sizes,
  },
}) => {
  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <div className={styles['container']} style={sizes} data-testid="mainpage-brand-desktop">
      <div className={styles['background']}>
        <img className={styles['background-image']} src={bgImageUrl} alt="" />
      </div>

      {!!label && (
        <div className={styles['label']}>
          <AdLabel label={label} />
        </div>
      )}

      <div className={styles['promo']} data-testid="promo">
        <div className={styles['promo-body']}>
          {!!sanitizedActionText && (
            <a
              className={styles['ribbon']}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              role="img"
              dangerouslySetInnerHTML={{ __html: sanitizedActionText }}
            />
          )}

          <div className={styles['offer']}>
            <a
              className={clsx(styles['logo'], !substrateNotNeeded && styles['logo--substrate'])}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              role="img"
            >
              <img src={logoImageUrl} alt="logo" />
            </a>

            <div className={styles['details']}>
              {(!!sanitizedName || !!sanitizedInfo) && (
                <a
                  className={styles['details-link']}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="button"
                >
                  {!!sanitizedName && (
                    <h3 className={styles['title']} dangerouslySetInnerHTML={{ __html: sanitizedName }} />
                  )}
                  {!!sanitizedInfo && (
                    <p className={styles['subtitle']} dangerouslySetInnerHTML={{ __html: sanitizedInfo }} />
                  )}
                </a>
              )}

              <div className={styles['actions']}>
                <a className={styles['button']} href={url} target="_blank" rel="noopener noreferrer">
                  {buttonText}
                </a>
                {!!sanitizedPhone && (
                  <span className={styles['phone']} dangerouslySetInnerHTML={{ __html: sanitizedPhone }} />
                )}
              </div>
            </div>
          </div>

          {!!note && (
            <div className={styles['note']}>
              <Ticker text={note} theme="light" withoutPadding playOnHover />
            </div>
          )}
        </div>

        {withMortgageAnnotations && (
          <div className={styles['mortgage-disclaimer']} data-testid="mortgage-disclaimer">
            <MortgageAnnotations
              height={sizes.height}
              mortgageWarning={mortgageWarning}
              mortgageTermsWarning={mortgageTermsWarning}
            />
          </div>
        )}
      </div>
    </div>
  );
};
